import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Plus, FileText, ClipboardList, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import { SortableHeader, useSort, makeComparator } from "@/components/SortableHeader";
import { BulkBar, SelectAllCheckbox, RowCheckbox } from "@/components/BulkBar";
import OrderCreateDialog from "@/components/OrderCreateDialog";
import OrderActionsDialog from "@/components/OrderActionsDialog";

const TAB_DEFS = [
  { value: "pending", label: "Pending orders", icon: ClipboardList, testid: "orders-tab-pending",
    desc: "Customer portal checkouts awaiting your action — review, then mark paid once they pay." },
  { value: "placed", label: "Placed orders", icon: ShoppingCart, testid: "orders-tab-placed",
    desc: "Quotes the customer accepted + admin-created orders. Mark paid to route to a ticket or invoice." },
  { value: "quote", label: "Quotes", icon: FileText, testid: "orders-tab-quotes",
    desc: "Draft & sent quotes waiting on customer acceptance." },
];

const STATUS_PILL = (s) => {
  const map = {
    draft_quote: "bg-secondary text-foreground",
    quote_sent: "bg-primary/10 text-primary",
    quote_accepted: "bg-amber-100 text-amber-800",
    placed: "bg-primary/10 text-primary",
    paid: "bg-amber-100 text-amber-800",
    fulfilled: "bg-success/10 text-success",
    declined: "bg-destructive/10 text-destructive",
    cancelled: "bg-muted text-muted-foreground",
  };
  return map[s] || "bg-muted text-muted-foreground";
};

const SOURCE_LABEL = (s) => ({
  admin_quote: "Quote",
  admin_order: "Admin",
  portal_order: "Portal",
}[s] || s);

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [actingOn, setActingOn] = useState(null);
  const [selected, setSelected] = useState([]);
  const { sortKey, sortDir, toggleSort } = useSort("created_at", "desc");

  // Reset selection when switching tabs so we never delete across lanes by accident.
  useEffect(() => { setSelected([]); }, [tab]);

  const load = useCallback(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch((e) => logError("orders load", e));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products?active_only=true").then((r) => setProducts(r.data));
  }, []);

  const filtered = useMemo(() => {
    let arr = orders.slice();
    if (tab === "pending") arr = arr.filter((o) => o.source === "portal_order" && o.status === "placed");
    else if (tab === "placed") arr = arr.filter((o) => o.source !== "portal_order" && (o.status === "placed" || o.status === "paid"));
    else if (tab === "quote") arr = arr.filter((o) => o.type === "quote" && ["draft_quote", "quote_sent"].includes(o.status));
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((o) =>
        (o.number || "").toLowerCase().includes(q) ||
        (o.business_name || o.customer_name || "").toLowerCase().includes(q) ||
        (o.notes || "").toLowerCase().includes(q),
      );
    }
    arr.sort(makeComparator((o) => {
      switch (sortKey) {
        case "number": return (o.number || "").toLowerCase();
        case "customer": return (o.business_name || o.customer_name || "").toLowerCase();
        case "delivery_date": return o.delivery_date || "";
        case "total": return Number(o.total || 0);
        case "status": return (o.status || "").toLowerCase();
        case "created_at":
        default: return o.created_at || "";
      }
    }, sortDir));
    return arr;
  }, [orders, tab, search, sortKey, sortDir]);

  const counts = useMemo(() => ({
    pending: orders.filter((o) => o.source === "portal_order" && o.status === "placed").length,
    placed: orders.filter((o) => o.source !== "portal_order" && (o.status === "placed" || o.status === "paid")).length,
    quote: orders.filter((o) => o.type === "quote" && ["draft_quote", "quote_sent"].includes(o.status)).length,
  }), [orders]);

  const showBulk = tab === "pending" || tab === "placed";
  const filteredIds = filtered.map((o) => o.id);

  const bulkDelete = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} order${ids.length === 1 ? "" : "s"}? Paid/fulfilled orders will be skipped.`)) return;
    try {
      const r = await api.post("/orders/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted}${r.data.skipped ? ` · ${r.data.skipped} skipped (already paid/fulfilled)` : ""}`);
      setSelected([]);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="overline">Sales</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">Orders &amp; Quotes</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            {TAB_DEFS.find((t) => t.value === tab)?.desc}
          </p>
        </div>
        <OrderCreateDialog customers={customers} products={products} onCreated={load} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <Tabs value={tab} onValueChange={setTab} className="overflow-x-auto -mx-1 px-1 sm:overflow-visible">
          <TabsList className="rounded-sm">
            {TAB_DEFS.map(({ value, label, testid }) => (
              <TabsTrigger key={value} value={value} data-testid={testid} className="rounded-sm">
                {label} ({counts[value]})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by number, customer, notes…"
                 className="rounded-sm pl-9" data-testid="orders-search" />
        </div>
      </div>

      {showBulk && (
        <BulkBar selected={selected} allIds={filteredIds} onChangeSelected={setSelected}
                 onBulkDelete={bulkDelete} testId="orders-bulk-bar" />
      )}

      <div className="border border-border rounded-sm overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline bg-secondary/40">
            <div className="col-span-1">
              {showBulk && <SelectAllCheckbox selected={selected} allIds={filteredIds} onChange={setSelected} testId="orders-select-all" />}
            </div>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="number" onToggle={toggleSort} className="col-span-2" testId="orders-sort-number">Number</SortableHeader>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="customer" onToggle={toggleSort} className="col-span-3" testId="orders-sort-customer">Customer</SortableHeader>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="created_at" onToggle={toggleSort} className="col-span-2" testId="orders-sort-created">Source / Date</SortableHeader>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="total" onToggle={toggleSort} className="col-span-1" testId="orders-sort-total">Total</SortableHeader>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="status" onToggle={toggleSort} className="col-span-3 justify-end" testId="orders-sort-status">Status / Actions</SortableHeader>
          </div>
          {filtered.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="orders-empty">
              No {tab === "quote" ? "quotes" : tab + " orders"} yet.
            </div>
          )}
          {filtered.map((o) => (
            <div key={o.id}
                 className="grid grid-cols-12 px-6 py-4 border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors items-center"
                 data-testid={`order-row-${o.number}`}>
              <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                {showBulk && <RowCheckbox id={o.id} selected={selected} onChange={setSelected} testId={`order-check-${o.number}`} />}
              </div>
              <button onClick={() => setActingOn(o)} className="col-span-11 grid grid-cols-11 text-left items-center" data-testid={`order-open-${o.number}`}>
                <div className="col-span-2 font-mono font-semibold">{o.number}</div>
                <div className="col-span-3">
                  <div className="font-medium">{o.business_name || o.customer_name}</div>
                  {o.business_name && o.customer_name && <div className="text-xs text-muted-foreground">{o.customer_name}</div>}
                </div>
                <div className="col-span-2 text-xs text-muted-foreground">
                  <div>{SOURCE_LABEL(o.source)}</div>
                  <div className="mono-num">{fmtDate(o.created_at?.slice(0, 10))}</div>
                </div>
                <div className="col-span-1 mono-num font-semibold">{fmtMoney(o.total)}</div>
                <div className="col-span-3 flex justify-end">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${STATUS_PILL(o.status)}`} data-testid={`order-status-${o.number}`}>
                    {o.status.replace("_", " ")}
                  </span>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {actingOn && (
        <OrderActionsDialog
          order={actingOn}
          onClose={() => setActingOn(null)}
          onChanged={(updated) => {
            setActingOn(updated || null);
            load();
          }}
        />
      )}
    </div>
  );
}
